<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: Presentational
  Reason: The document with a draw layer over it, the toolbar that pages and zooms, the role the
  next rectangle is assigned to, and the list of everything placed so far.
  NOTE: nothing here shows, hints at or offers a choice of evidence level or authentication method
  (BA-2) - both are derived on the server from the identification act actually performed.
-->
@if (!document) {

    <!-- No bytes to draw on. A state, not a failure. -->
    <div class="flex flex-column align-items-center gap-2 p-5 text-center border-1 border-300
                border-round-lg surface-50">
        <i class="fa-regular fa-file-pdf text-3xl text-gray-500"></i>
        <span class="text-lg font-medium">Es liegt kein Dokument zur Platzierung vor.</span>
        <span class="text-sm text-gray-600">
            Der Signaturprozess trägt kein Quelldokument.
        </span>
    </div>

} @else {

    <div class="flex flex-column gap-3">

        <!-- The interaction was never specified. Said out loud rather than left to look decided. -->
        <p-message severity="secondary"
                   text="Das Bedienkonzept dieses Werkzeugs ist fachlich noch nicht abgestimmt — UX-Review ausstehend."
                   styleClass="w-full">
        </p-message>

        <!-- ========================================================== -->
        <!-- Toolbar: role, pages, zoom                                 -->
        <!-- ========================================================== -->
        <div class="flex flex-wrap align-items-center gap-2 p-2 border-1 border-300 border-round-lg
                    surface-50">

            <label class="text-sm font-medium white-space-nowrap" for="sm-placement-role">
                Rolle für neue Rechtecke
            </label>

            <p-select inputId="sm-placement-role"
                      [options]="roleOptions"
                      [(ngModel)]="activeRoleKey"
                      optionLabel="label"
                      optionValue="value"
                      appendTo="body"
                      placeholder="Rolle wählen"
                      styleClass="w-14rem"
                      [disabled]="disabled || saving || roleOptions.length === 0">
            </p-select>

            <p-divider layout="vertical" styleClass="mx-2 h-1rem"></p-divider>

            <!-- Pages -->
            <p-button icon="fa-regular fa-chevron-left"
                      severity="secondary"
                      [text]="true"
                      size="small"
                      pTooltip="Vorherige Seite"
                      tooltipPosition="top"
                      ariaLabel="Vorherige Seite"
                      [disabled]="!canGoBack"
                      (onClick)="goToPreviousPage()"></p-button>

            <span class="text-sm font-medium white-space-nowrap">
                Seite {{ pageNumber }} von {{ pageCount }}
            </span>

            <p-button icon="fa-regular fa-chevron-right"
                      severity="secondary"
                      [text]="true"
                      size="small"
                      pTooltip="Nächste Seite"
                      tooltipPosition="top"
                      ariaLabel="Nächste Seite"
                      [disabled]="!canGoForward"
                      (onClick)="goToNextPage()"></p-button>

            @if (pageCount > 1) {
                <p-select [options]="pageOptions"
                          [ngModel]="pageNumber"
                          optionLabel="label"
                          optionValue="value"
                          appendTo="body"
                          styleClass="w-9rem"
                          pTooltip="Direkt zu einer Seite springen"
                          tooltipPosition="top"
                          (onChange)="goToPage($event.value)">
                </p-select>
            }

            <p-divider layout="vertical" styleClass="mx-2 h-1rem"></p-divider>

            <!-- Zoom -->
            <p-button icon="fa-regular fa-magnifying-glass-minus"
                      severity="secondary"
                      [text]="true"
                      size="small"
                      pTooltip="Verkleinern"
                      tooltipPosition="top"
                      ariaLabel="Verkleinern"
                      (onClick)="zoomOut()"></p-button>

            <span class="text-sm white-space-nowrap w-4rem text-center">{{ zoomPercent }} %</span>

            <p-button icon="fa-regular fa-magnifying-glass-plus"
                      severity="secondary"
                      [text]="true"
                      size="small"
                      pTooltip="Vergrößern"
                      tooltipPosition="top"
                      ariaLabel="Vergrößern"
                      (onClick)="zoomIn()"></p-button>

            <p-button label="An Breite anpassen"
                      icon="fa-regular fa-arrows-left-right"
                      severity="secondary"
                      [text]="true"
                      size="small"
                      (onClick)="fitWidth()"></p-button>

            <p-button label="Seite neu aufbauen"
                      icon="fa-regular fa-arrows-rotate"
                      severity="secondary"
                      [text]="true"
                      size="small"
                      (onClick)="retryRender()"></p-button>

            <div class="flex-grow-1"></div>

            <p-button label="Platzierungen speichern"
                      icon="fa-regular fa-floppy-disk"
                      size="small"
                      [loading]="saving"
                      [disabled]="!canSave"
                      (onClick)="emitSave()"></p-button>
        </div>

        <!-- Why drawing is not possible right now. One sentence per reason, never a silent layer. -->
        @if (renderState === 'ready' && !geometryMismatch && !disabled && !activeRoleKey) {
            <p-message severity="info"
                       text="Bitte wählen Sie zuerst die Rolle, der die nächsten Rechtecke zugeordnet werden."
                       styleClass="w-full">
            </p-message>
        }

        @if (geometryMismatch) {
            <p-message severity="warn"
                       text="Die Seitengeometrie dieser Seite stimmt nicht mit der des Servers überein. Auf dieser Seite können keine Rechtecke platziert werden, weil die gespeicherten Koordinaten sonst eine andere Stelle bezeichnen würden."
                       styleClass="w-full">
            </p-message>
        }

        @if (drawWarning) {
            <p-message severity="warn" [text]="drawWarning" styleClass="w-full"></p-message>
        }

        @if (renderState === 'error' && renderError) {
            <p-message severity="error" [text]="renderError" styleClass="w-full"></p-message>
        }

        <!-- ========================================================== -->
        <!-- The page, with the draw layer over it                      -->
        <!-- ========================================================== -->
        <div #stageViewport class="sm-placement-viewport border-1 border-300 border-round-lg p-3 surface-100">

            <div class="sm-placement-stage"
                 [style.width.px]="canvasCssWidth || null"
                 [style.height.px]="canvasCssHeight || null">

                <canvas #pageCanvas class="sm-placement-canvas"></canvas>

                <!-- The layer every drag is measured against. Exactly congruent with the canvas,
                     never scaled by a transform of its own. -->
                <div #drawLayer
                     class="sm-placement-draw-layer"
                     [class.sm-placement-draw-layer--armed]="canDraw"
                     (pointerdown)="onPointerDown($event)"
                     (pointermove)="onPointerMove($event)"
                     (pointerup)="onPointerUp($event)"
                     (pointercancel)="onPointerCancel()">

                    <!-- Everything already placed on this page. Inert: deleting happens in the
                         list, so a box can never swallow the drag that is meant to start on it. -->
                    @for (marker of markers; track marker.draft.draftId) {
                        <div class="sm-placement-marker"
                             [class.sm-placement-marker--unassigned]="!marker.draft.roleKey"
                             [style.left.px]="marker.left"
                             [style.top.px]="marker.top"
                             [style.width.px]="marker.width"
                             [style.height.px]="marker.height">
                            <span class="sm-placement-marker-label">
                                {{ marker.ordinal }} · {{ marker.draft.roleKey ? roleLabel(marker.draft.roleKey) : 'Ohne Rolle' }}
                            </span>
                        </div>
                    }

                    <!-- The rectangle currently being dragged. -->
                    @if (draftBox) {
                        <div class="sm-placement-draft"
                             [style.left.px]="draftBox.left"
                             [style.top.px]="draftBox.top"
                             [style.width.px]="draftBox.width"
                             [style.height.px]="draftBox.height">
                        </div>
                    }
                </div>

                @if (renderState === 'loading') {
                    <div class="sm-placement-busy">
                        <p-progressSpinner styleClass="w-3rem h-3rem"></p-progressSpinner>
                        <span class="text-sm">Die Seite wird aufgebaut …</span>
                    </div>
                }
            </div>
        </div>

        <!-- ========================================================== -->
        <!-- The list of everything placed                              -->
        <!-- ========================================================== -->
        <div class="flex flex-column gap-2">

            <div class="flex align-items-center gap-2">
                <h5 class="m-0 font-semibold">Platzierte Unterschriftsfelder</h5>
                <p-tag [value]="drafts.length + ''" severity="secondary"></p-tag>
                @if (unassignedCount > 0) {
                    <p-tag [value]="unassignedCount + ' ohne Rolle'" severity="warn"></p-tag>
                }
            </div>

            @if (drafts.length === 0) {
                <div class="flex flex-column align-items-center gap-2 p-4 text-center border-1
                            border-300 border-round-lg surface-50">
                    <i class="fa-regular fa-vector-square text-2xl text-gray-500"></i>
                    <span class="text-sm font-medium">Es wurde noch kein Unterschriftsfeld platziert.</span>
                    <span class="text-sm text-gray-600">
                        Wählen Sie eine Rolle und ziehen Sie ein Rechteck auf dem Dokument auf.
                    </span>
                </div>
            } @else {
                <p-table [value]="drafts" styleClass="p-datatable-sm" [scrollable]="true">
                    <ng-template pTemplate="header">
                        <tr>
                            <th class="w-3rem">Nr.</th>
                            <th class="w-5rem">Seite</th>
                            <th>Rolle</th>
                            <th>Position (PDF-Punkte)</th>
                            <th class="w-8rem">Aktion</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-draft>
                        <tr>
                            <td>{{ ordinalOf(draft) }}</td>
                            <td>
                                <p-button [label]="draft.page + ''"
                                          severity="secondary"
                                          [text]="true"
                                          size="small"
                                          pTooltip="Seite anzeigen"
                                          tooltipPosition="top"
                                          (onClick)="showDraft(draft)"></p-button>
                            </td>
                            <td>
                                <p-select [options]="roleOptions"
                                          [ngModel]="draft.roleKey"
                                          optionLabel="label"
                                          optionValue="value"
                                          appendTo="body"
                                          placeholder="Rolle wählen"
                                          styleClass="w-14rem"
                                          [disabled]="disabled || saving"
                                          (onChange)="assignRole(draft, $event.value)">
                                </p-select>
                            </td>
                            <!-- Shortened for READING only. The stored value keeps every digit the
                                 conversion produced; rounding happens nowhere but in this cell. -->
                            <td class="sm-placement-technical text-sm">
                                {{ draft.rect.lowerLeftX | number:'1.2-2' }},
                                {{ draft.rect.lowerLeftY | number:'1.2-2' }} →
                                {{ draft.rect.upperRightX | number:'1.2-2' }},
                                {{ draft.rect.upperRightY | number:'1.2-2' }}
                            </td>
                            <td>
                                <p-button label="Entfernen"
                                          icon="fa-regular fa-trash"
                                          severity="danger"
                                          [text]="true"
                                          size="small"
                                          [disabled]="disabled || saving"
                                          (onClick)="removeDraft(draft)"></p-button>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            }
        </div>
    </div>
}
